// packages/shared/src/components/scheduling/TimePreferenceFields.jsx (moved from apps/provider 2026-10-21; also used by the patient app's waitlist panel)
// Patient time preference editor shared by the appointment wizard (Provider
// step) and the waitlist entry form. Edits a windows list + excluded days; the
// availability engine owns the semantics (containment, union, anchors).

import React from 'react';
import {
  TIME_WINDOW_PRESETS,
  windowsEqual,
  isPresetWindow,
  minutesToTime,
  timeToMinutes,
} from '../../utils/availabilityEngine';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TimePreferenceFields = ({
  windows = [],
  excludedDays = [],
  onChange,
  label = 'Patient Time Restrictions',
  description = 'Select windows when the patient is available, or a specific time with an optional end time. Leave empty for no restriction.',
  daysLabel = 'Days Patient Cannot Attend',
  daysDescription = 'Click days the patient is not available. Leave empty for no restriction.',
}) => {
  const emit = (next) => onChange?.({ windows: next.windows ?? windows, excludedDays: next.excludedDays ?? excludedDays });

  // At most one non-preset window: the "specific time" row
  const custom = windows.find(w => !isPresetWindow(w)) || null;
  const presets = windows.filter(w => isPresetWindow(w));
  const endBeforeStart = custom && custom._invalidEnd;

  const togglePreset = (preset) => {
    const isActive = windows.some(w => windowsEqual(w, preset));
    const next = isActive
      ? windows.filter(w => !windowsEqual(w, preset))
      : [...windows, { start: preset.start, end: preset.end }];
    emit({ windows: next });
  };

  const setCustom = (nextCustom) => {
    emit({ windows: nextCustom ? [...presets, nextCustom] : presets });
  };

  const handleStartChange = (value) => {
    if (!value) return setCustom(null);
    const start = timeToMinutes(value);
    const end = custom?.end != null && custom.end > start ? custom.end : null;
    setCustom({ start, end });
  };

  const handleEndChange = (value) => {
    if (!custom) return;
    if (!value) return setCustom({ start: custom.start, end: null });
    const end = timeToMinutes(value);
    if (end <= custom.start) return setCustom({ start: custom.start, end: null, _invalidEnd: true });
    setCustom({ start: custom.start, end });
  };

  const toggleDay = (index) => {
    const next = excludedDays.includes(index)
      ? excludedDays.filter(d => d !== index)
      : [...excludedDays, index];
    emit({ excludedDays: next });
  };

  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <p className="text-xs text-gray-400 mb-2">{description}</p>
        <div className="flex flex-wrap gap-2">
          {TIME_WINDOW_PRESETS.map(preset => {
            const isActive = windows.some(w => windowsEqual(w, preset));
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => togglePreset(preset)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  isActive
                    ? 'bg-teal-100 text-teal-700 border-teal-300'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-gray-500">or specific time:</span>
          <input
            type="time"
            value={custom ? minutesToTime(custom.start) : ''}
            onChange={(e) => handleStartChange(e.target.value)}
            step="900"
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-[#0facd7] focus:border-[#0facd7]"
          />
          <span className="text-xs text-gray-500">to</span>
          <input
            type="time"
            value={custom?.end != null ? minutesToTime(custom.end) : ''}
            onChange={(e) => handleEndChange(e.target.value)}
            disabled={!custom}
            step="900"
            title={custom ? 'Optional end time. Leave empty to require this exact start time.' : 'Set a start time first'}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-[#0facd7] focus:border-[#0facd7] disabled:bg-gray-50 disabled:text-gray-400"
          />
          {custom && (
            <button
              type="button"
              onClick={() => setCustom(null)}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear
            </button>
          )}
        </div>
        {custom && (
          <p className={`text-xs mt-1 ${endBeforeStart ? 'text-red-600' : 'text-gray-400'}`}>
            {endBeforeStart
              ? 'End time must be after the start time.'
              : custom.end == null
                ? 'Exact start time. Add an end time to allow any slot inside a range.'
                : 'Any slot that starts and ends inside this range.'}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{daysLabel}</label>
        {daysDescription && <p className="text-xs text-gray-400 mb-2">{daysDescription}</p>}
        <div className="flex gap-2">
          {DAY_LABELS.map((dayLabel, index) => {
            const isExcluded = excludedDays.includes(index);
            return (
              <button
                key={index}
                type="button"
                onClick={() => toggleDay(index)}
                className={`w-10 h-10 rounded-full text-sm font-medium border transition-colors ${
                  isExcluded
                    ? 'bg-red-100 text-red-500 border-red-300 line-through'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                {dayLabel}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default TimePreferenceFields;
